/**
 * Connection Status Banner
 *
 * Shows a subtle banner above the message input when the socket
 * is disconnected or reconnecting. Replaces the invisible HTTP
 * fallback with transparent user feedback.
 *
 * Three states:
 * - connecting/disconnected: spinner + "Reconnecting..."
 * - failed: "Connection lost" + "Tap to reconnect" button
 * - session_expired: "Session expired" + "Sign in again" button (triggers logout)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useThemeColors } from '../../theme';
import { useSocketStatus, ensureConnected } from '../../services/socket';
import { useAuthStore } from '../../store/authStore';

/** Show "Taking longer than usual..." after this many ms of continuous reconnecting */
const SLOW_RECONNECT_THRESHOLD_MS = 15_000;

export const ConnectionStatusBanner = React.memo(function ConnectionStatusBanner() {
  const { t } = useTranslation();
  const theme = useThemeColors();
  const status = useSocketStatus((s) => s.status);
  const disconnectedAt = useSocketStatus((s) => s.disconnectedAt);
  const [isSlow, setIsSlow] = useState(false);

  // Flip to "slow" after 15s of continuous disconnection
  // Intentional: deriving slow-reconnect state from external disconnectedAt timestamp
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!disconnectedAt) {
      setIsSlow(false);
      return;
    }
    const elapsed = Date.now() - disconnectedAt;
    const remaining = SLOW_RECONNECT_THRESHOLD_MS - elapsed;

    if (remaining <= 0) {
      setIsSlow(true);
      return;
    }

    const timer = setTimeout(() => setIsSlow(true), remaining);
    return () => clearTimeout(timer);
  }, [disconnectedAt]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleReconnect = useCallback(() => {
    ensureConnected();
  }, []);

  const handleSignIn = useCallback(() => {
    useAuthStore.getState().logout();
  }, []);

  if (status === 'connected') return null;

  // Session expired — user must sign in again
  if (status === 'session_expired') {
    return (
      <TouchableOpacity
        testID="chat-connection-banner"
        accessibilityRole="button"
        accessibilityLabel={`${t('chat.connection.sessionExpired')}. ${t('chat.connection.signInAgain')}`}
        onPress={handleSignIn}
        activeOpacity={0.7}
        style={[styles.container, { backgroundColor: theme.error + '20', borderColor: theme.error + '40' }]}
      >
        <Text style={[styles.text, { color: theme.error }]}>
          {t('chat.connection.sessionExpired')}
        </Text>
        <Text style={[styles.retryText, { color: theme.error }]}>
          {t('chat.connection.signInAgain')}
        </Text>
      </TouchableOpacity>
    );
  }

  // Connection failed — user can tap to retry
  if (status === 'failed') {
    return (
      <TouchableOpacity
        testID="chat-connection-banner"
        accessibilityRole="button"
        accessibilityLabel={`${t('chat.connection.failed')}. ${t('chat.connection.tapToReconnect')}`}
        onPress={handleReconnect}
        activeOpacity={0.7}
        style={[styles.container, { backgroundColor: theme.error + '20', borderColor: theme.error + '40' }]}
      >
        <Text style={[styles.text, { color: theme.error }]}>
          {t('chat.connection.failed')}
        </Text>
        <Text style={[styles.retryText, { color: theme.error }]}>
          {t('chat.connection.tapToReconnect')}
        </Text>
      </TouchableOpacity>
    );
  }

  // Connecting/disconnected — auto-reconnecting with spinner
  // When slow, make the banner tappable so the user can force a fresh reconnect
  // instead of waiting for Socket.IO's automatic cycle to exhaust all attempts.
  if (isSlow) {
    return (
      <TouchableOpacity
        testID="chat-connection-banner"
        accessibilityRole="button"
        accessibilityLabel={`${t('chat.connection.reconnectingSlow')}. ${t('chat.connection.tapToReconnect')}`}
        onPress={handleReconnect}
        activeOpacity={0.7}
        style={[styles.container, { backgroundColor: theme.warning + '20', borderColor: theme.warning + '40' }]}
      >
        <ActivityIndicator size="small" color={theme.warning} style={styles.spinner} />
        <Text style={[styles.text, { color: theme.warning }]}>
          {t('chat.connection.reconnectingSlow')}
        </Text>
        <Text style={[styles.retryText, { color: theme.warning }]}>
          {t('chat.connection.tapToReconnect')}
        </Text>
      </TouchableOpacity>
    );
  }

  return (
    <View
      testID="chat-connection-banner"
      accessibilityRole="alert"
      accessibilityLabel={t('chat.connection.reconnecting')}
      style={[styles.container, { backgroundColor: theme.warning + '20', borderColor: theme.warning + '40' }]}
    >
      <ActivityIndicator size="small" color={theme.warning} style={styles.spinner} />
      <Text style={[styles.text, { color: theme.warning }]}>
        {t('chat.connection.reconnecting')}
      </Text>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginHorizontal: 16,
    marginBottom: 4,
    borderRadius: 8,
    borderWidth: 1,
  },
  spinner: {
    marginRight: 6,
  },
  text: {
    fontSize: 12,
    fontWeight: '500',
  },
  retryText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 6,
    textDecorationLine: 'underline',
  },
});
